import axios from 'axios';
import { getProjectEnvVariables } from '@/shared/projectEnvVariables';
import {
  FEEDBACK_TYPE_LABELS,
  type ChatwootPublicContact,
  type ChatwootPublicConversation,
  type FeedbackContext,
  type FeedbackReporter,
  type FeedbackSubmission,
} from './_models';

// Base for Chatwoot's public (widget) API. The inbox_identifier is the same
// non-secret token the website widget exposes, so these calls are safe from the
// browser. We use a raw axios (not the app instance) because this is a different
// host with no auth header and no ApiResponse envelope to unwrap.
const inboxApiUrl = () => {
  const { VITE_APP_CHATWOOT_BASE_URL, VITE_APP_CHATWOOT_INBOX_IDENTIFIER } = getProjectEnvVariables().envVariables;
  const base = VITE_APP_CHATWOOT_BASE_URL.replace(/\/+$/, '');
  return `${base}/public/api/v1/inboxes/${VITE_APP_CHATWOOT_INBOX_IDENTIFIER}`;
};

// Chatwoot rejects the contact unless phone_number is E.164. Account phones come
// in BR display formats ("(83) 99999-0000"); normalize the ones we recognize and
// return undefined for anything else, so a malformed number is dropped instead of
// failing the whole submission. Bare 10/11-digit numbers are assumed BR (+55) —
// correct for this POS admin's user base. The raw value still shows in the footer.
export const toE164 = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, '').replace(/^0+/, '');
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    return `+${digits}`; // already carries the BR country code
  }
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`; // bare DDD + landline/mobile
  }
  return undefined;
};

// Renders the feedback into the message body: just the type, the description and
// the page it came from. The reporter's identity is NOT repeated here — it lives
// on the Chatwoot contact. Pure, so it stays unit-testable without the network.
export const buildFeedbackContent = ({ type, message }: FeedbackSubmission, context?: FeedbackContext): string => {
  const lines = [`**${FEEDBACK_TYPE_LABELS[type]}**`, '', message.trim()];
  if (context?.page) {
    lines.push('', '---', `*Página:* ${context.page}`);
  }
  return lines.join('\n');
};

// Three-step public handshake: create contact -> open conversation -> post the
// message. The reporter's identity (from useAuth) is stamped on the contact so
// support sees who wrote in; `identifier` dedupes repeat feedback onto a single
// contact. Returns the conversation id; network/HTTP errors bubble up.
export const sendFeedbackToChatwoot = async (
  feedback: FeedbackSubmission,
  reporter?: FeedbackReporter,
  context?: FeedbackContext,
): Promise<number> => {
  const api = inboxApiUrl();
  const replyEmail = feedback.email?.trim() || reporter?.email;

  const { data: contact } = await axios.post<ChatwootPublicContact>(`${api}/contacts`, {
    identifier: reporter?.id,
    name: reporter?.name || replyEmail,
    email: replyEmail,
    phone_number: toE164(reporter?.phone),
    custom_attributes: {
      role: reporter?.role,
      environment_id: reporter?.environmentId,
      account_id: reporter?.id,
    },
  });

  const { data: conversation } = await axios.post<ChatwootPublicConversation>(
    `${api}/contacts/${contact.source_id}/conversations`,
    {},
  );

  await axios.post(`${api}/contacts/${contact.source_id}/conversations/${conversation.id}/messages`, {
    content: buildFeedbackContent(feedback, context),
  });

  return conversation.id;
};
